import { useEffect, useMemo, useState } from "react";
import {
  BellRing,
  ChevronRight,
  CirclePause,
  CirclePlay,
  FlaskConical,
  LoaderCircle,
  Mail,
  MessageCircleMore,
  MoreHorizontal,
  Pencil,
  Plus,
  Plug,
  Search,
  Smartphone,
  Trash2,
} from "lucide-react";
import {
  CHANNEL_LABELS,
  EVENT_LABELS,
  FILTER_MODES,
  SAVED_EXPEDIENT_MODES,
} from "@tribunalea/shared";
import { useApi } from "../App.jsx";
import { StatusBadge } from "../components/StatusBadge.jsx";
import { EmptyState } from "../components/EmptyState.jsx";
import { ConfirmDialog } from "../components/ConfirmDialog.jsx";
import { AlertsSectionNav } from "../components/AlertsSectionNav.jsx";
import { PageHeader, useRouter } from "@tribunalea/ui";

const channelIcons = {
  email: Mail,
  sms: Smartphone,
  whatsapp: MessageCircleMore,
};

function RuleFilter({ rule }) {
  if (rule.filter.mode === FILTER_MODES.CUIJ) {
    return <span className="filter-description">CUIJ {rule.filter.cuij}</span>;
  }
  if (rule.filter.mode === FILTER_MODES.SAVED_EXPEDIENTES) {
    const selection = rule.filter.savedExpedientes;
    return (
      <span className="filter-description">
        {selection?.mode === SAVED_EXPEDIENT_MODES.SELECTED
          ? `${selection.cuijs?.length || 0} de Mis expedientes`
          : "Todos mis expedientes"}
      </span>
    );
  }
  if (rule.filter.mode === FILTER_MODES.FOLLOW_UPS) {
    const selection = rule.filter.followUps;
    return (
      <span className="filter-description">
        {selection?.mode === SAVED_EXPEDIENT_MODES.SELECTED
          ? `${selection.cuijs?.length || 0} de Mis seguimientos`
          : "Todos mis seguimientos"}
      </span>
    );
  }
  if (rule.filter.mode === FILTER_MODES.INTEGRATION) {
    return (
      <span className="filter-description">
        <Plug size={13} /> Integración · Notificaciones del PJM
      </span>
    );
  }
  const [first, ...rest] = rule.filter.jurisdictions;
  return (
    <span className="filter-description">
      {first
        ? `${first.circunscripcionLabel} · ${first.typeLabel} · ${first.courtLabel}`
        : "Sin combinaciones"}
      {rest.length > 0 && <em>+{rest.length}</em>}
    </span>
  );
}

function RuleCard({ rule, onEdit, onToggle, onDelete }) {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <article className="rule-card">
      <div className="rule-card-main">
        <span className="rule-icon">
          <BellRing size={20} />
        </span>
        <div className="rule-title-block">
          <div className="rule-title-row">
            <h3>{rule.name}</h3>
            <StatusBadge status={rule.status} />
          </div>
          <RuleFilter rule={rule} />
        </div>
        <div className="event-column">
          <span className="table-label">Evento</span>
          <strong>{EVENT_LABELS[rule.event.type]}</strong>
          {rule.event.stuckBusinessDays && (
            <small>{rule.event.stuckBusinessDays} días hábiles</small>
          )}
        </div>
        <div className="channel-column">
          <span className="table-label">Canales</span>
          <div className="channel-icons">
            {rule.channels.map((channel) => {
              const Icon = channelIcons[channel];
              return (
                <span key={channel} title={CHANNEL_LABELS[channel]}>
                  <Icon size={17} />
                </span>
              );
            })}
          </div>
        </div>
        <div className="recipient-column">
          <span className="table-label">Destinatarios</span>
          <strong>
            {rule.recipients.mode === "all"
              ? "Todo el equipo"
              : `${rule.recipients.userIds.length} ${
                  rule.recipients.userIds.length === 1 ? "persona" : "personas"
                }`}
          </strong>
        </div>
        <div className="rule-actions">
          <button
            className="icon-button"
            onClick={() => setMenuOpen((value) => !value)}
            aria-label={`Acciones de ${rule.name}`}
          >
            <MoreHorizontal size={19} />
          </button>
          {menuOpen && (
            <div className="action-menu">
              <button onClick={onEdit}>
                <Pencil size={15} /> Editar
              </button>
              <button onClick={onToggle}>
                {rule.status === "active" ? (
                  <CirclePause size={15} />
                ) : (
                  <CirclePlay size={15} />
                )}
                {rule.status === "active" ? "Pausar" : "Reanudar"}
              </button>
              <button className="danger-text" onClick={onDelete}>
                <Trash2 size={15} /> Eliminar
              </button>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

export function NotificationsPage() {
  const api = useApi();
  const { navigate } = useRouter();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [developmentRun, setDevelopmentRun] = useState({
    status: "idle",
    message: "",
  });

  const load = async () => {
    try {
      setError("");
      setRules(await api.listRules());
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const visibleRules = useMemo(
    () =>
      rules.filter((rule) => {
        const matchesSearch = rule.name.toLowerCase().includes(search.toLowerCase());
        const matchesFilter = filter === "all" || rule.status === filter;
        return matchesSearch && matchesFilter;
      }),
    [rules, search, filter],
  );

  const toggleRule = async (rule) => {
    const status = rule.status === "active" ? "paused" : "active";
    await api.updateRule(rule._id, { status });
    setRules((current) =>
      current.map((item) => (item._id === rule._id ? { ...item, status } : item)),
    );
  };

  const removeRule = async () => {
    await api.deleteRule(deleteTarget._id);
    setRules((current) => current.filter((rule) => rule._id !== deleteTarget._id));
    setDeleteTarget(null);
  };

  const runDevelopmentScrape = async () => {
    setDevelopmentRun({
      status: "running",
      message: "Ejecutando el scraping y evaluando las alertas…",
    });
    try {
      const result = await api.runDevelopmentNotificationScrape();
      const failedProcessors = result.eventResults.filter(
        ({ status }) => status === "failed",
      ).length;
      const hasPartialFailures =
        result.status === "partial_failed" || failedProcessors > 0;
      const failureSummary = [
        result.refresh.failures.length
          ? `${result.refresh.failures.length} error${
              result.refresh.failures.length === 1 ? "" : "es"
            } de scraping`
          : "",
        failedProcessors
          ? `${failedProcessors} procesador${
              failedProcessors === 1 ? "" : "es"
            } con error`
          : "",
      ]
        .filter(Boolean)
        .join(" y ");
      setDevelopmentRun({
        status: hasPartialFailures ? "warning" : "success",
        message: hasPartialFailures
          ? `La ejecución actualizó ${
              result.refresh.expedientesUpdated
            } expediente${
              result.refresh.expedientesUpdated === 1 ? "" : "s"
            } y terminó con ${failureSummary || "errores parciales"}.`
          : `Ejecución completada: ${
              result.refresh.expedientesUpdated
            } expediente${
              result.refresh.expedientesUpdated === 1 ? "" : "s"
            } actualizado${
              result.refresh.expedientesUpdated === 1 ? "" : "s"
            } y ${result.eventResults.length} evento${
              result.eventResults.length === 1 ? "" : "s"
            } evaluado${result.eventResults.length === 1 ? "" : "s"}.`,
      });
    } catch (requestError) {
      setDevelopmentRun({
        status: "error",
        message: requestError.message,
      });
    }
  };

  const activeCount = rules.filter((rule) => rule.status === "active").length;

  return (
    <div className="page">
      <PageHeader
        eyebrow="Praxis"
        title="Alertas judiciales"
        description="Seguí expedientes y recibí novedades donde las necesitás."
        actions={(
          <>
          {import.meta.env.DEV && (
            <button
              className="button secondary development-run-button"
              onClick={runDevelopmentScrape}
              disabled={developmentRun.status === "running"}
              title="Ejecutar ahora el mismo proceso configurado en el cron"
            >
              {developmentRun.status === "running" ? (
                <LoaderCircle className="spin" size={18} />
              ) : (
                <FlaskConical size={18} />
              )}
              {developmentRun.status === "running"
                ? "Ejecutando…"
                : "Probar monitoreo"}
            </button>
          )}
          <button
            className="button primary"
            onClick={() => navigate("/alertas/nueva")}
          >
            <Plus size={18} />
            Nueva alerta
          </button>
          </>
        )}
      />

      <AlertsSectionNav />

      {import.meta.env.DEV && developmentRun.status !== "idle" && (
        <div
          className={`development-run-status ${developmentRun.status}`}
          role="status"
          aria-live="polite"
        >
          <FlaskConical size={17} />
          <span>{developmentRun.message}</span>
        </div>
      )}

      <section className="summary-strip">
        <div>
          <span className="summary-number">{rules.length}</span>
          <span>Alertas configuradas</span>
        </div>
        <div>
          <span className="summary-number emerald">{activeCount}</span>
          <span>Activas ahora</span>
        </div>
        <div className="summary-message">
          <span className="live-dot" />
          El monitoreo automático está configurado
        </div>
      </section>

      <section className="rules-section">
        <div className="section-toolbar">
          <div className="tabs">
            {[
              ["all", "Todas"],
              ["active", "Activas"],
              ["paused", "Pausadas"],
            ].map(([value, label]) => (
              <button
                key={value}
                className={filter === value ? "active" : ""}
                onClick={() => setFilter(value)}
              >
                {label}
              </button>
            ))}
          </div>
          <label className="search-field">
            <Search size={17} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar alerta"
              aria-label="Buscar alerta"
            />
          </label>
        </div>

        {error && (
          <div className="inline-error">
            <span>{error}</span>
            <button onClick={load}>Reintentar</button>
          </div>
        )}

        {loading ? (
          <div className="loading-list">
            <span />
            <span />
          </div>
        ) : visibleRules.length ? (
          <div className="rules-list">
            <div className="list-caption">
              <span>Alerta y alcance</span>
              <span>Configuración</span>
            </div>
            {visibleRules.map((rule) => (
              <RuleCard
                key={rule._id}
                rule={rule}
                onEdit={() => navigate(`/alertas/${rule._id}/editar`)}
                onToggle={() => toggleRule(rule)}
                onDelete={() => setDeleteTarget(rule)}
              />
            ))}
          </div>
        ) : rules.length ? (
          <div className="no-results">
            <Search size={22} />
            <h2>No encontramos alertas</h2>
            <p>Probá con otro término o filtro.</p>
          </div>
        ) : (
          <EmptyState onCreate={() => navigate("/alertas/nueva")} />
        )}
      </section>

      <button
        className="help-card"
        onClick={() => navigate("/alertas/historial")}
      >
        <span>
          <strong>¿Querés comprobar los últimos envíos?</strong>
          <small>Consultá el historial y el estado de cada canal.</small>
        </span>
        <ChevronRight size={20} />
      </button>

      {deleteTarget && (
        <ConfirmDialog
          title="Eliminar alerta"
          description={`“${deleteTarget.name}” dejará de monitorearse. Esta acción no elimina el historial de envíos.`}
          confirmLabel="Eliminar alerta"
          onConfirm={removeRule}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
